import {
  DatePickerInputField,
  InputField,
  RadioField,
  SelectField,
  TextareaField,
} from '../../../../components/FormikFields';
import CheckboxField from '../../../../components/FormikFields/CheckboxField';
import CurrencyField from '../../../../components/FormikFields/CurrencyField';
import Switch from '../../../../components/FormikFields/Switch';
import VatInputField from '../../../../components/FormikFields/VatInputField';
import {
  getProductWithTotals,
  validateItemFields,
} from '../../../../helpers/invoiceHelpers';
import { LogoUpload } from './components/LogoUpload/LogoUpload';
import { defaultItemData } from './formConfig/initialValues';
import { v4 as uuid } from 'uuid';

export const getInputType = (type) => {
  switch (type) {
    case 'text': {
      return InputField;
    }
    case 'vat': {
      return VatInputField;
    }
    case 'select': {
      return SelectField;
    }
    case 'datepicker': {
      return DatePickerInputField;
    }
    case 'textarea': {
      return TextareaField;
    }
    case 'file': {
      return LogoUpload;
    }
    case 'switch': {
      return Switch;
    }
    case 'radio': {
      return RadioField;
    }
    case 'check': {
      return CheckboxField;
    }
    case 'currency': {
      return CurrencyField;
    }
  }
};

export const getComponentProps = (input) => {
  const {
    onChange,
    onBlur,
    onKeyPress,
    options,
    disabled,
    id,
    name,
    disabledDays,
    maxlength,
    type,
    minLength,
    updateFieldsNames,
    verifyVat,
    placeholder,
    placeholderKey,
    inputMode,
    pattern,
    forceVerifyVat,
    t,
  } = input;
  const componentProps = {
    disabled,
    id,
    name,
    type,
  };
  if (onChange) {
    componentProps.onChange = onChange;
  }
  if (onBlur) {
    componentProps.onBlur = onBlur;
  }
  if (onKeyPress) {
    componentProps.onKeyPress = onKeyPress;
  }
  if (options) {
    componentProps.options = options;
  }
  if (disabledDays) {
    componentProps.disabledDays = disabledDays;
  }
  if (maxlength) {
    componentProps.maxlength = maxlength;
  }
  if (minLength) {
    componentProps.minLength = minLength;
  }
  if (updateFieldsNames) {
    componentProps.updateFieldsNames = updateFieldsNames;
  }
  if (verifyVat) {
    componentProps.verifyVat = verifyVat;
  }
  if (forceVerifyVat) {
    componentProps.forceVerifyVat = forceVerifyVat;
  }
  if (inputMode) {
    componentProps.inputMode = inputMode;
  }
  if (pattern) {
    componentProps.pattern = pattern;
  }
  if (placeholderKey && t) {
    componentProps.placeholder = t(placeholderKey);
  } else if (placeholder) {
    componentProps.placeholder = placeholder;
  }

  return componentProps;
};

export const handleAddItemClick = async ({
  values,
  validateForm,
  setTouched,
  setErrors,
  setValues,
  t,
}) => {
  const hasError = await validateItemFields({
    setTouched,
    validateForm,
    setErrors,
    item: values?.tempItem ?? {},
    t,
  });

  if (hasError) {
    return;
  }

  values.tempItem.quantity = Number(values.tempItem.quantity).toFixed(2);
  values.tempItem.discountAmount = Number(
    values.tempItem.discountAmount,
  ).toFixed(3);
  values.tempItem.vatAmount = Number(values.tempItem.vatAmount).toFixed(3);

  const currentItem = getProductWithTotals({
    item: values.tempItem,
  });

  currentItem.creating = true;

  const formValues = {
    ...values,
    items: [...values.items, currentItem],
    tempItem: { id: uuid(), ...defaultItemData() },
  };

  setValues(formValues);

  return formValues;
};

export const getTaxTypePlaceholder = (lang) =>
  lang === 'en'
    ? 'Please select the discount VAT rate'
    : 'يرجى اختيار نسبة القيمة المضافة المطبقة على الخصم';

export const getTaxTypes = (lang, showPlaceholder = true) => {
  return lang === 'en'
    ? [
        showPlaceholder && {
          value: '',
          label: getTaxTypePlaceholder(lang),
        },
        { value: 'Z', label: '0%', rate: '0' },
        { value: '5', label: '5%', rate: '5' },
        { value: '10', label: '10%', rate: '10' },
        { value: 'E', label: 'Exempt', rate: '0' },
        { value: 'O', label: 'Out of scope', rate: '0' },
      ].filter(Boolean)
    : [
        showPlaceholder && {
          value: '',
          label: getTaxTypePlaceholder(lang),
        },
        { value: 'Z', label: '0%', rate: '0' },
        { value: '5', label: '5%', rate: '5' },
        { value: '10', label: '10%', rate: '10' },
        { value: 'E', label: 'معفى', rate: '0' },
        { value: 'O', label: 'خارج نطاق الضريبة', rate: '0' },
      ].filter(Boolean);
};

export const getTaxTypeValue = (value) => {
  if (value === 'E' || value === 'O' || value === 'Z') {
    return 0;
  }
  return +value;
};

export const normalizeDecimalValue = (value, maxDecimalPlaces = 3) => {
  const sanitizedValue = String(value ?? '').replace(/[^\d.]/g, '');
  const [integerPart = '', ...decimalParts] = sanitizedValue.split('.');
  const decimalPart = decimalParts.join('').slice(0, maxDecimalPlaces);

  return decimalParts.length > 0
    ? `${integerPart}.${decimalPart}`
    : integerPart;
};
